import { useMutation, useQuery } from '@tanstack/react-query';
import { administrationClient, unwrap, createResource } from '@/shared/api-client';

export interface User {
  id: string;
  email: string;
  displayName: string | null;
  isActive: boolean;
  mustChangePassword: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserPage {
  items: User[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CreateUserInput {
  email: string;
  displayName?: string;
  password: string;
}

export interface UpdateUserInput {
  email?: string;
  displayName?: string;
  isActive?: boolean;
}

export interface ListUsersParams {
  search?: string;
  page?: number;
  pageSize?: number;
}

export const listUsers = (params: ListUsersParams = {}): Promise<UserPage> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/users', {
      params: { query: params },
    }),
  ) as Promise<UserPage>;

export const createUser = (input: CreateUserInput): Promise<User> =>
  unwrap(
    administrationClient.POST('/api/v1/franchise-administration/users', { body: input }),
  ) as Promise<User>;

export const updateUser = (id: string, input: UpdateUserInput): Promise<User> =>
  unwrap(
    administrationClient.PATCH('/api/v1/franchise-administration/users/{id}', {
      params: { path: { id } },
      body: input,
    }),
  ) as Promise<User>;

export const forceLogout = (id: string): Promise<void> =>
  unwrap(
    administrationClient.POST('/api/v1/franchise-administration/users/{id}/force-logout', {
      params: { path: { id } },
    }),
  );

export interface ResetPasswordResult {
  tempPassword: string;
}

export const resetPassword = (id: string): Promise<ResetPasswordResult> =>
  unwrap(
    administrationClient.POST('/api/v1/franchise-administration/users/{id}/reset-password', {
      params: { path: { id } },
    }),
  ) as Promise<ResetPasswordResult>;

export interface RegeneratePosPinResult {
  posPin: string;
}

export const regeneratePosPin = (id: string): Promise<RegeneratePosPinResult> =>
  unwrap(
    administrationClient.POST('/api/v1/franchise-administration/users/{id}/regenerate-pos-pin', {
      params: { path: { id } },
    }),
  ) as Promise<RegeneratePosPinResult>;

/** Users list + create/update through the shared resource factory (ADR 0012).
 *  The typed fetch closures stay per-module; the react-query ribbon is the
 *  factory's, and the bespoke session mutations below compose on its
 *  `useInvalidate`. */
const users = createResource({
  key: ['users'],
  list: listUsers,
  create: createUser,
  update: ({ id, input }: { id: string; input: UpdateUserInput }) => updateUser(id, input),
});

export const useUsers = users.useList;
export const useCreateUser = users.useCreate;
export const useUpdateUser = users.useUpdate;

// force-logout and reset-password are deliberately no-invalidate: neither alters
// any field the users list renders, and reset-password returns the one-time temp
// password the dialog must surface rather than discard behind a refetch.
export function useForceLogout() {
  return useMutation({ mutationFn: forceLogout });
}

export function useResetPassword() {
  return useMutation({ mutationFn: resetPassword });
}

export function useRegeneratePosPin() {
  return useMutation({ mutationFn: regeneratePosPin });
}

// --- Role assignments (S9) -------------------------------------------------

export interface VenueAccessItem {
  venueId: string;
  venueName: string | null;
}

export type AssignmentVenueAccess =
  | { kind: 'admin_implicit_all' }
  | { kind: 'selected'; venues: VenueAccessItem[]; unresolved: boolean };

/** A single resolved role assignment row with its VenueAccess read model. */
export interface RoleAssignment {
  id: string;
  userId: string;
  roleId: string;
  roleName: string;
  venueAccess: AssignmentVenueAccess;
  createdAt: string;
}

export interface RoleAssignmentList {
  items: RoleAssignment[];
}

export type AssignmentVenueAccessInput = { kind: 'selected'; venueIds: string[] };

export interface CreateRoleAssignmentInput {
  roleId: string;
  venueAccess: AssignmentVenueAccessInput;
}

// The generated schema lags the hand-written VenueAccess assignment contract in this slice.
// Keep the module-level types as the source of truth for the JSON and cast at the generated seam.
export const listRoleAssignments = (userId: string): Promise<RoleAssignmentList> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/users/{userId}/role-assignments', {
      params: { path: { userId } },
    }),
  ) as unknown as Promise<RoleAssignmentList>;

export const createRoleAssignment = (
  userId: string,
  input: CreateRoleAssignmentInput,
): Promise<RoleAssignmentList> =>
  unwrap(
    administrationClient.POST('/api/v1/franchise-administration/users/{userId}/role-assignments', {
      params: { path: { userId } },
      body: input as never,
    }),
  ) as unknown as Promise<RoleAssignmentList>;

export const updateRoleAssignmentVenueAccess = (
  id: string,
  venueAccess: AssignmentVenueAccessInput,
): Promise<RoleAssignment> =>
  unwrap(
    administrationClient.PATCH('/api/v1/franchise-administration/role-assignments/{id}', {
      params: { path: { id } },
      body: { venueAccess } as never,
    }),
  ) as unknown as Promise<RoleAssignment>;

export const revokeRoleAssignment = (id: string): Promise<void> =>
  unwrap(
    administrationClient.DELETE('/api/v1/franchise-administration/role-assignments/{id}', {
      params: { path: { id } },
    }),
  );

/** Role assignments as a sub-resource. The `list` closure types the per-user
 *  `keys.list(userId)` key; the bespoke hooks below reuse the compose surface —
 *  `keys` and `useInvalidate` (the whole `['role-assignments']` prefix) — instead
 *  of re-deriving query keys. The list query itself stays bespoke because it must
 *  gate on `enabled` (the user-detail view passes `null` until a user is open). */
const roleAssignments = createResource({
  key: ['role-assignments'],
  list: (userId?: string) => listRoleAssignments(userId ?? ''),
});

export function useRoleAssignments(userId: string | null) {
  return useQuery({
    queryKey: roleAssignments.keys.list(userId ?? ''),
    queryFn: () => listRoleAssignments(userId!),
    enabled: userId != null,
  });
}

export function useCreateRoleAssignment(userId: string) {
  const invalidate = roleAssignments.useInvalidate();
  return useMutation({
    mutationFn: (input: CreateRoleAssignmentInput) => createRoleAssignment(userId, input),
    onSuccess: invalidate,
  });
}

export function useUpdateRoleAssignmentVenueAccess() {
  const invalidate = roleAssignments.useInvalidate();
  return useMutation({
    mutationFn: ({ id, venueAccess }: { id: string; venueAccess: AssignmentVenueAccessInput }) =>
      updateRoleAssignmentVenueAccess(id, venueAccess),
    onSuccess: invalidate,
  });
}

export function useRevokeRoleAssignment() {
  const invalidate = roleAssignments.useInvalidate();
  return useMutation({ mutationFn: revokeRoleAssignment, onSuccess: invalidate });
}

// --- Unresolved VenueAccess summary (ADR 0033) --------------------------

/** One affected user surfaced by the unresolved-summary read model. */
export interface UnresolvedAffectedUser {
  userId: string;
  email: string;
  displayName: string | null;
  unresolvedAssignmentCount: number;
}

/** Backend-owned read model for the Users sidebar warning and the "Needs venue access" tab. */
export interface UnresolvedVenueAccessSummary {
  unresolvedAssignmentCount: number;
  affectedUserCount: number;
  affectedUsers: UnresolvedAffectedUser[];
}

export const getUnresolvedVenueAccessSummary = (): Promise<UnresolvedVenueAccessSummary> =>
  unwrap(
    administrationClient.GET(
      '/api/v1/franchise-administration/role-assignments/unresolved-summary',
    ),
  ) as unknown as Promise<UnresolvedVenueAccessSummary>;

/** The summary keys off the whole `['role-assignments']` prefix so assignment edits refresh it.
 *  Gate with `enabled` where the caller may lack `role_assignment.view` (e.g. the sidebar). */
export function useUnresolvedVenueAccessSummary(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: roleAssignments.keys.list('unresolved-summary'),
    queryFn: getUnresolvedVenueAccessSummary,
    enabled: options.enabled ?? true,
  });
}
